export const getAlignmentClasses = (alignment?: string) => {
  switch (alignment) {
    case 'left':
      return 'items-start text-left';
    case 'right':
      return 'items-end text-right';
    case 'center':
    default:
      return 'items-center text-center';
  }
};

export const getIntroAlignmentClasses = (alignment?: string) => {
  switch (alignment) {
    case 'right':
      return {
        container: 'text-right ml-auto',
        buttons: 'justify-end',
      };
    case 'left':
      return {
        container: 'text-left mr-auto',
        buttons: 'justify-start',
      };
    case 'center':
    default:
      return {
        container: 'text-center mx-auto',
        buttons: 'justify-center',
      };
  }
};

export const getSectionProps = (
  component: { sectionId?: string; moduleId?: number; type?: string; cmsModuleName?: string } & Record<string, unknown>,
  componentPosition?: number,
  isNested?: boolean
) => ({
  id: component.sectionId ?? (component.moduleId ? `c${component.moduleId}` : undefined),
  'data-module': component.type,
  'data-cms-module': component.cmsModuleName,
  ...(!isNested && { 'data-position': componentPosition }),
});

/**
 * Builds the stable tracking namespace for a module: "<type>-<moduleId>"
 * (e.g. "cta-section-147"). The moduleId (TYPO3 uid) is what makes the key
 * unique across repeated instances of the same module on one page — two
 * cta-sections become "cta-section-147" and "cta-section-203". Falls back to
 * just the type when no moduleId is present. Never derive this from CMS text.
 * Consumers append the element segment (e.g. "-cta", "-card-2-cta").
 */
export const getTrackingPrefix = (component: {
  type?: string;
  moduleId?: number;
}): string | undefined => {
  if (!component.type) return undefined;
  return component.moduleId != null
    ? `${component.type}-${component.moduleId}`
    : component.type;
};

/**
 * Resolves the trailing item segment of a `data-track-id` (the part after the
 * element name, e.g. the `481` in `overview-203-card-481`).
 *
 * Prefers the item's own TYPO3 uid — like the module `moduleId`, it survives
 * reordering and deletion, so an item's analytics history stays intact even
 * when a redactor moves it within the list. Falls back to the 1-based position
 * only when the item carries no own record uid (CTAs, nav links, plain text
 * features, persons). A truthy check (not `??`) is intentional: a missing uid
 * arrives as `undefined`/`null`/`0`/`''`, all of which must fall through to the
 * index.
 */
export const getItemTrackId = (
  id: string | number | null | undefined,
  index: number
): string | number =>
  id === null || id === undefined || id === 0 || id === ''
    ? index + 1
    : id;

/**
 * True when a RichText HTML string already contains an inline `<a>` link. Cards
 * use this to skip wrapping the whole card in a link (a nested `<a>` inside an
 * `<a>` is invalid HTML) — an inline RichText link then takes precedence.
 */
export const richTextHasInlineLinks = (html?: string): boolean =>
  !!html && (html.includes('<a ') || html.includes('<a>'));

export type Breakpoint = 'sm' | 'md' | 'lg' | 'xl' | 'landscape';

/**
 * Returns grid position classes based on media position settings.
 * Controls column and row placement for mobile and desktop layouts.
 */
export const getPositionClasses = (
  isRight: boolean,
  isBottom: boolean,
  isFirstColumn: boolean,
  breakpoint: Breakpoint = 'lg'
): string => {
  const bp = breakpoint;

  const col = isFirstColumn
    ? isRight
      ? `${bp}:col-start-2`
      : `${bp}:col-start-1`
    : isRight
      ? `${bp}:col-start-1`
      : `${bp}:col-start-2`;

  const row = isFirstColumn
    ? isBottom
      ? 'row-start-2'
      : 'row-start-1'
    : isBottom
      ? 'row-start-1'
      : 'row-start-2';

  return `${col} ${row} ${bp}:row-start-1`;
};
